import { useState, useEffect, useRef } from 'react';

import { MarketplaceOffer } from '@the-real-insight/tri-model';
import { useModalContext } from '../components/ModalProvider';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';

import { AlertCircle, Plus, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface SampleRequestsProps {
    type: any;
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
}

export default function SampleRequests({
    type: _type,
    marketplaceOffer,
    setMarketplaceOffer,
}: SampleRequestsProps) {
    const { t } = useTranslation();
    const { pushError: _pushError } = useModalContext() as any;
    const [_authentication] = useAtom(authenticationAtom) as any;
    const [newRequestText, setNewRequestText] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const textFieldRef = useRef<any>(null);

    // Debug: Monitor changes to sampleRequests
    useEffect(() => {
        console.log('sampleRequests changed:', marketplaceOffer.sampleRequests);
        console.log('sampleRequests length:', marketplaceOffer.sampleRequests?.length || 0);
    }, [marketplaceOffer.sampleRequests]);

    const _formatToolName = (toolName: string) => {
        const lowerName = toolName.toLowerCase();
        
        if (lowerName.includes('knowledge') || lowerName.includes('wissensbasis') || lowerName.includes('rag')) {
            return `${t('ui.knowledgeBase')} ${toolName}`;
        } else if (lowerName.includes('prompt') || lowerName.includes('werkzeug')) {
            return `${t('ui.promptTool')} ${toolName}`;
        } else if (lowerName.includes('workflow') || lowerName.includes('agentic')) {
            return `${t('ui.agenticWorkflow')} ${toolName}`;
        } else {
            return toolName;
        }
    };

    const validateRequest = (requestText: string): string[] => {
        const errors: string[] = [];
        if (!requestText.trim()) {
            errors.push(t('ui.sampleRequestRequired'));
        }
        return errors;
    };

    const addSampleRequest = () => {
        console.log('addSampleRequest called, newRequestText:', newRequestText);
        const errors = validateRequest(newRequestText);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        const newRequest = newRequestText.trim();
        if (!newRequest) {
            return;
        }
        
        console.log('Adding new request:', newRequest);
        console.log('Current sampleRequests:', marketplaceOffer.sampleRequests);
        
        // Create a new marketplaceOffer object with the updated sampleRequests
        // Ensure we create a completely new array reference
        const currentSampleRequests = marketplaceOffer.sampleRequests || [];
        const updatedSampleRequests = [...currentSampleRequests, newRequest];
        
        const updatedMarketplaceOffer = {
            ...marketplaceOffer,
            sampleRequests: updatedSampleRequests
        } as MarketplaceOffer;
        
        console.log('Updated marketplaceOffer:', updatedMarketplaceOffer);
        console.log('Updated sampleRequests array:', updatedSampleRequests);
        console.log('Updated sampleRequests length:', updatedSampleRequests.length);
        console.log('Calling setMarketplaceOffer');
        
        // Update the marketplace offer first
        setMarketplaceOffer(updatedMarketplaceOffer);
        
        // Then clear the input and errors
        setNewRequestText('');
        setValidationErrors([]);
        
        console.log('addSampleRequest completed');
    };

    const removeSampleRequest = (index: number) => {
        console.log('removeSampleRequest called', index);
        // Create a new marketplaceOffer object with the updated sampleRequests
        const updatedMarketplaceOffer = {
            ...marketplaceOffer,
            sampleRequests: (marketplaceOffer.sampleRequests || []).filter((_: string, i: number) => i !== index)
        } as MarketplaceOffer;
        setMarketplaceOffer(updatedMarketplaceOffer);
    };

    return (
        <div className="w-full max-w-[600px]">
            <div className="mb-4">
                <div className="text-base font-semibold">{t('ui.sampleRequestsPanelTitle')}</div>
                <div className="mt-3 text-sm text-muted-foreground">
                    {t('ui.sampleRequestsPanelHelp')}
                </div>
            </div>

            {validationErrors.length > 0 && (
                <Alert variant="destructive" className="mb-4">
                    <AlertCircle aria-hidden className="h-4 w-4" />
                    <AlertDescription>
                        {validationErrors.map((error, index) => (
                            <div key={index}>{error}</div>
                        ))}
                    </AlertDescription>
                </Alert>
            )}

            <div className="mb-6 flex gap-2">
                <div className="min-w-0 grow">
                    <Input
                        ref={textFieldRef}
                        aria-label={t('ui.sampleRequestsPlaceholder')}
                        placeholder={t('ui.sampleRequestsPlaceholder')}
                        value={newRequestText}
                        onChange={(e) => {
                            setNewRequestText(e.target.value || '');
                            if (validationErrors.length > 0) {
                                setValidationErrors([]);
                            }
                        }}
                        onKeyDown={(e) => {
                            console.log('KeyDown event:', e.key, e.shiftKey, e);
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Calling addSampleRequest from onKeyDown');
                                addSampleRequest();
                            }
                        }}
                        onKeyPress={(e) => {
                            console.log('KeyPress event:', e.key, e.shiftKey, e);
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Calling addSampleRequest from onKeyPress');
                                addSampleRequest();
                            }
                        }}
                    />
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={addSampleRequest}
                    disabled={!newRequestText.trim()}
                    title={t('ui.addSampleRequest')}
                    aria-label={t('ui.addSampleRequest')}
                >
                    <Plus aria-hidden className="h-4 w-4" />
                </Button>
            </div>

            {(marketplaceOffer.sampleRequests || []).length === 0 ? (
                <div className="rounded-md border bg-muted p-5 text-center text-xs text-muted-foreground">
                    {t('ui.noSampleRequestsAvailable')}
                </div>
            ) : (
                <div>
                    {(marketplaceOffer.sampleRequests || []).map((request: string, index: number) => {
                        return (
                            <div key={index} className="mb-1 rounded-md border bg-muted py-2 pl-4 pr-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0 grow">
                                        <div className="mb-0.5 break-words text-sm font-semibold">{request}</div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeSampleRequest(index)}
                                        title={t('ui.deleteSampleRequest')}
                                        aria-label={t('ui.deleteSampleRequest')}
                                    >
                                        <Trash2 aria-hidden className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
} 